/**
 * API Client Base Factory
 *
 * Creates configured Axios instances with token interceptors and
 * error handling. Used by both employee-app and customer-app to
 * create their own API clients with different auth token sources.
 */

import axios, { AxiosInstance, AxiosError } from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_ENDPOINT || '/api';

export interface ApiClientOptions {
  /** Function that returns the auth token (or null if not authenticated) */
  getToken: () => Promise<string | null> | string | null;
  /** Called when a 401 response is received (e.g., redirect to login) */
  onUnauthorized: () => void;
  /** Optional base URL override */
  baseUrl?: string;
}

/**
 * Create a configured Axios instance with auth interceptors.
 */
export function createApiClient(options: ApiClientOptions): AxiosInstance {
  const client = axios.create({
    baseURL: options.baseUrl || API_BASE_URL,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Add auth token to requests
  client.interceptors.request.use(async (config) => {
    const token = await options.getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  // Handle errors
  client.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
      if (error.response?.status === 401) {
        options.onUnauthorized();
      }
      return Promise.reject(error);
    }
  );

  return client;
}

export { API_BASE_URL };
